// Ported verbatim from the LeadMailer Design System (components/core/Button.jsx).
import React from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ControlSize = 'sm' | 'md' | 'lg'

const sizes: Record<ControlSize, React.CSSProperties> = {
  sm: { height: 'var(--control-h-sm)', padding: '0 12px', fontSize: 'var(--text-sm)' },
  md: { height: 'var(--control-h-md)', padding: '0 14px', fontSize: 'var(--text-base)' },
  lg: { height: 'var(--control-h-lg)', padding: '0 18px', fontSize: 'var(--text-md)' },
}

const variants: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    // Gradient rather than a flat fill, so the one call-to-action on a screen
    // reads as the same object as the dashboard's gradient glyph tiles. Both
    // stops are the existing accent ramp — no new colour enters here.
    background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
    color: 'var(--text-on-accent)',
    border: '1px solid var(--accent)',
  },
  secondary: {
    background: 'var(--surface-card)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
    boxShadow: 'var(--shadow-xs)',
  },
  ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent' },
  danger: { background: 'var(--danger)', color: '#fff', border: '1px solid var(--danger)' },
}

const hoverBgs: Record<ButtonVariant, string> = {
  primary: 'var(--accent-hover)',
  secondary: 'var(--surface-sunken)',
  ghost: 'var(--surface-sunken)',
  danger: 'var(--danger-hover)',
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ControlSize
  icon?: React.ReactNode
  fullWidth?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  disabled,
  fullWidth,
  children,
  style,
  ...rest
}: ButtonProps) {
  const [hover, setHover] = React.useState(false)
  const [press, setPress] = React.useState(false)
  const v = variants[variant] ?? variants.primary
  const hoverBg = hoverBgs[variant]

  return (
    <button
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false)
        setPress(false)
      }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontFamily: 'var(--font-sans)',
        fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
        borderRadius: 'var(--radius-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition:
          'background var(--dur-fast) var(--ease-out),transform var(--dur-fast) var(--ease-out)',
        transform: press ? 'scale(.98)' : 'none',
        width: fullWidth ? '100%' : undefined,
        whiteSpace: 'nowrap',
        ...sizes[size],
        ...v,
        ...(hover && !disabled
          ? { background: hoverBg, color: variant === 'ghost' ? 'var(--text-primary)' : v.color }
          : {}),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
}
